import { SxProps, Theme } from '@mui/material';
import { WARNING_MAIN } from '../../../styles/GlobalStyles';
import { StepStatus } from './QuizNavigator';

export const QUIZ_NAVIGATOR_SKIPPED_BADGE_COLOR = WARNING_MAIN;
export const QUIZ_NAVIGATOR_ANSWERED_FILL_COLOR = '#1890fa';
export const QUIZ_NAVIGATOR_PROGRESS_COLOR = '#94D2FF';

export const quizNavigatorStyles = {
  container: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    padding: '0 10px',
    gap: '10px',
  }) as SxProps<Theme>,

  previousButton: (_theme: Theme) => ({
    display: 'flex',
    justifyContent: 'flex-start',
  }) as SxProps<Theme>,

  button: (theme: Theme) => ({
    fontSize: '18px',
    fontWeight: 500,
    minHeight: '34px',
    padding: '0 4px',
    boxSizing: 'border-box',
    transform: 'none',
    transition: 'none',
    '&&': {
      border: '2px solid transparent',
      outline: 'none',
      boxShadow: 'none',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      transform: 'none',
      transition: 'none',
    },
    '&&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&&:active': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.dark}`,
      outlineOffset: '2px',
    },
    '&&.Mui-disabled': {
      opacity: 0.65,
      cursor: 'not-allowed',
      border: '2px solid #d7d7d7',
      backgroundColor: '#d7d7d7',
      color: theme.palette.common.white,
    },
  }) as SxProps<Theme>,

  step: (theme: Theme, isClickable: boolean) => ({
    cursor: isClickable ? 'pointer' : 'default',
    outline: 'none',
    '&:focus-visible .MuiStepLabel-iconContainer': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '2px',
      borderRadius: '50%',
    },
  }) as SxProps<Theme>,

  stepper: (theme: Theme, lastSeenIndex: number, totalSteps: number): SxProps<Theme> => {
    const stepWidth = 100 / totalSteps;
    const progressPercent = (lastSeenIndex / totalSteps) * 100 + stepWidth * 0.5;
    const progressStop = `${progressPercent}%`;

    return {
      flex: 1,
      overflow: 'hidden',
      boxSizing: 'border-box',
      border: 'none',
      borderRadius: '3px',
      minHeight: '28px',
      padding: theme.spacing(1, 0),
      background: `linear-gradient(to right, ${QUIZ_NAVIGATOR_PROGRESS_COLOR} ${progressStop}, ${theme.palette.grey[100]} ${progressStop})`,
      '& .MuiStepper-root': {
        backgroundColor: 'transparent',
      },
      '& .MuiStepConnector-root': {
        display: 'none',
      },
    };
  },

  nextButton: (_theme: Theme) => ({
    display: 'flex',
    justifyContent: 'flex-end',
  }) as SxProps<Theme>,

  stepLabel: (theme: Theme, status: StepStatus, isCurrent: boolean, isSkipped: boolean = false): SxProps<Theme> => {
    const isAnswered = status === 'submitted';

    let fill: string;
    let border: string;

    if (isCurrent && isAnswered) {
      fill = QUIZ_NAVIGATOR_ANSWERED_FILL_COLOR;
      border = `1px solid ${QUIZ_NAVIGATOR_SKIPPED_BADGE_COLOR}`;
    } else if (isCurrent) {
      fill = 'white';
      border = `1px solid ${QUIZ_NAVIGATOR_SKIPPED_BADGE_COLOR}`;
    } else if (isAnswered) {
      fill = QUIZ_NAVIGATOR_ANSWERED_FILL_COLOR;
      border = '1px solid transparent';
    } else if (isSkipped) {
      fill = QUIZ_NAVIGATOR_SKIPPED_BADGE_COLOR;
      border = `1px solid ${QUIZ_NAVIGATOR_SKIPPED_BADGE_COLOR}`;
    } else {
      fill = 'white';
      border = '1px solid #666666';
    }

    return {
      '& .MuiStepLabel-iconContainer': {
        '& .MuiStepIcon-root': {
          boxSizing: 'content-box',
          color: fill,
          border,
          borderRadius: '50%',
          width: '12px',
          height: '12px',
        },
        '& .MuiStepIcon-text': {
          display: 'none',
        },
      },
    };
  },
};
